import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Shield, Heart, MapPin, BadgeCheck, Users, Sparkles, Lock, Eye, EyeOff,
  UserPlus, LogIn, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Combined page:
 *  - If URL has ?token=... and no active session → show accept flow with optional
 *    "Create Wali account" form (choose to just view once, or register a real
 *    account with password).
 *  - If user is authenticated as `wali` (no token needed) → show dashboard.
 *  - Fallback → error / redirect to login.
 */
export default function WaliView() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const isWaliLoggedIn = user && typeof user === "object" && user.role === "wali";

  // -------- If token-based flow --------
  if (token && !isWaliLoggedIn) {
    return <TokenAcceptFlow token={token} onLoggedIn={async () => { await refresh(); navigate("/wali", { replace: true }); }} />;
  }

  // -------- If already logged in as wali --------
  if (isWaliLoggedIn) {
    return <WaliDashboard />;
  }

  // -------- Fallback --------
  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="wali-error">
      <Lock className="h-8 w-8 text-brown-500 mx-auto" />
      <h1 className="mt-4 font-serif text-3xl text-olive-700">Ingen adgang</h1>
      <p className="mt-3 text-brown-600 text-sm">
        Denne side kræver enten et gyldigt Wali-invitationslink eller at du er logget ind som Wali.
      </p>
      <Link to="/login" className="btn-primary mt-6 inline-flex" data-testid="wali-goto-login">
        <LogIn className="h-4 w-4" /> Log ind
      </Link>
    </div>
  );
}

// ---------- Token accept flow ----------
function TokenAcceptFlow({ token, onLoggedIn }) {
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [showRegister, setShowRegister] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [regErr, setRegErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        await axios.post(`${API}/family/accept`, { token }).catch(() => {});
        const { data } = await axios.get(`${API}/family/view`, { params: { token } });
        setState({ loading: false, data, err: "" });
      } catch (e) {
        setState({ loading: false, data: null, err: e?.response?.data?.detail || "Ugyldigt eller udløbet link." });
      }
    })();
  }, [token]);

  const register = async (e) => {
    e.preventDefault();
    setRegErr("");
    if (pw.length < 8) { setRegErr("Adgangskoden skal være mindst 8 tegn."); return; }
    if (pw !== pw2) { setRegErr("Adgangskoderne matcher ikke."); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/family/accept`, { token, password: pw }, { withCredentials: true });
      if (res.data?.account) {
        await onLoggedIn();
      } else {
        setRegErr("Kunne ikke oprette konto — prøv igen.");
      }
    } catch (e) {
      setRegErr(e?.response?.data?.detail || "Kontoen kunne ikke oprettes.");
    } finally { setBusy(false); }
  };

  if (state.loading) return <div className="max-w-3xl mx-auto px-4 py-24 text-brown-500 text-center">Åbner Wali-oversigten…</div>;

  if (state.err || !state.data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="wali-error">
        <Lock className="h-8 w-8 text-brown-500 mx-auto" />
        <h1 className="mt-4 font-serif text-3xl text-olive-700">Adgang nægtet</h1>
        <p className="mt-3 text-brown-600 text-sm">{state.err}</p>
        <Link to="/" className="btn-secondary mt-6 inline-flex">Til forsiden</Link>
      </div>
    );
  }

  const { wali, user, stats, recent_matches } = state.data;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="wali-view">
      <div className="card-soft p-6 sm:p-8 bg-gradient-to-br from-olive-50 to-sand-50 border-olive-500/20">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-olive-700 text-gold-400 flex items-center justify-center"><Shield className="h-6 w-6" /></div>
          <div>
            <div className="eyebrow">Wali-oversigt</div>
            <h1 className="mt-1 font-serif text-3xl text-olive-700">Assalamu alaikum, {wali.name}</h1>
            <p className="text-sm text-brown-600 mt-1">
              Du følger <strong>{user.display_name || user.name}</strong>'s rejse på Find Your Naseeb som <em>{wali.relation}</em>.
            </p>
          </div>
        </div>

        {/* Account creation prompt */}
        {!showRegister ? (
          <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-gold-100/50 border border-gold-500/30 rounded-xl p-4" data-testid="wali-upgrade-cta">
            <UserPlus className="h-5 w-5 text-gold-700 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-brown-700">Vil du modtage notifikationer i realtid?</div>
              <div className="text-xs text-brown-500 mt-0.5">
                Opret en Wali-konto med adgangskode så du får besked, når {user.display_name || user.name} får en ny henvendelse eller et bekræftet match — uden at skulle finde linket igen.
              </div>
            </div>
            <button onClick={() => setShowRegister(true)} className="btn-primary text-sm whitespace-nowrap" data-testid="wali-create-account-btn">
              Opret Wali-konto <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <form onSubmit={register} className="mt-6 bg-white/70 border border-sand-300 rounded-xl p-4 space-y-3" data-testid="wali-register-form">
            <div className="text-sm font-medium text-brown-700 flex items-center gap-2"><UserPlus className="h-4 w-4 text-gold-700" /> Opret din Wali-konto</div>
            <div className="text-xs text-brown-500">E-mail: <strong>{wali.email}</strong></div>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Adgangskode (min. 8 tegn)"
                className="field w-full pr-10"
                data-testid="wali-pw-1"
                required
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brown-400" tabIndex={-1}>
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <input
              type={showPw ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Bekræft adgangskode"
              className="field w-full"
              data-testid="wali-pw-2"
              required
            />
            {regErr && <div className="text-destructive text-xs">{regErr}</div>}
            <div className="flex gap-2">
              <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="wali-register-submit">
                {busy ? "Opretter…" : "Opret konto og log ind"}
              </button>
              <button type="button" onClick={() => setShowRegister(false)} className="btn-secondary text-sm">Annullér</button>
            </div>
          </form>
        )}

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Matches" value={stats.matches} icon={<Sparkles className="h-4 w-4" />} testid="stat-matches" />
          <Stat label="Introduktioner sendt" value={stats.introductions_sent} icon={<Heart className="h-4 w-4" />} testid="stat-sent" />
          <Stat label="Introduktioner modtaget" value={stats.introductions_received} icon={<Users className="h-4 w-4" />} testid="stat-received" />
          <Stat label="Accepterede" value={stats.introductions_accepted} icon={<BadgeCheck className="h-4 w-4" />} testid="stat-accepted" />
        </div>

        <PrivacyNote />
      </div>

      <MatchList matches={recent_matches} />
      <FooterNote />
    </div>
  );
}

// ---------- Session-based dashboard (logged-in wali) ----------
function WaliDashboard() {
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/family/dashboard");
        setState({ loading: false, data, err: "" });
      } catch (e) {
        setState({ loading: false, data: null, err: formatApiError(e) });
      }
    })();
  }, []);

  if (state.loading) return <div className="max-w-3xl mx-auto px-4 py-24 text-brown-500 text-center">Åbner dit Wali-dashboard…</div>;

  if (state.err || !state.data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="wali-dashboard-error">
        <Lock className="h-8 w-8 text-brown-500 mx-auto" />
        <h1 className="mt-4 font-serif text-3xl text-olive-700">Kunne ikke indlæse</h1>
        <p className="mt-3 text-brown-600 text-sm">{state.err}</p>
      </div>
    );
  }

  const { wali, user, stats, recent_matches } = state.data;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="wali-dashboard">
      <div className="card-soft p-6 sm:p-8 bg-gradient-to-br from-olive-50 to-sand-50 border-olive-500/20">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-olive-700 text-gold-400 flex items-center justify-center"><Shield className="h-6 w-6" /></div>
          <div>
            <div className="eyebrow">Wali-dashboard</div>
            <h1 className="mt-1 font-serif text-3xl text-olive-700">Assalamu alaikum, {wali.name}</h1>
            <p className="text-sm text-brown-600 mt-1">
              Du følger <strong>{user.display_name || user.name}</strong>'s rejse som <em>{wali.relation || "familie"}</em>. Du modtager notifikationer i realtid.
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Matches" value={stats.matches} icon={<Sparkles className="h-4 w-4" />} testid="dash-stat-matches" />
          <Stat label="Introduktioner sendt" value={stats.introductions_sent} icon={<Heart className="h-4 w-4" />} testid="dash-stat-sent" />
          <Stat label="Introduktioner modtaget" value={stats.introductions_received} icon={<Users className="h-4 w-4" />} testid="dash-stat-received" />
          <Stat label="Accepterede" value={stats.introductions_accepted} icon={<BadgeCheck className="h-4 w-4" />} testid="dash-stat-accepted" />
        </div>

        <PrivacyNote />
      </div>

      <MatchList matches={recent_matches} />
      <FooterNote />
    </div>
  );
}

function PrivacyNote() {
  return (
    <div className="mt-6 flex items-start gap-2 text-xs text-brown-500 bg-white/60 border border-sand-300 rounded-xl p-3">
      <Lock className="h-4 w-4 shrink-0 mt-0.5" />
      <div>
        Du får en respektfuld, read-only oversigt. Private beskeder forbliver private mellem parterne — som det bør være.
      </div>
    </div>
  );
}

function MatchList({ matches }) {
  return (
    <div className="mt-10">
      <h2 className="font-serif text-2xl text-olive-700">Seneste forslag</h2>
      {matches.length === 0 ? (
        <div className="mt-4 text-brown-500 text-sm" data-testid="wali-no-matches">Ingen matches endnu — vi opdaterer dette efterhånden som samtaler opstår.</div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {matches.map((m) => (
            <div key={m.match_id} className="card-soft p-4" data-testid={`wali-match-${m.match_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-serif text-lg text-olive-700 flex items-center gap-1.5">
                  {m.display_name || "—"}{m.age ? `, ${m.age}` : ""}
                  {m.verified && <BadgeCheck className="h-4 w-4 text-sky-600 fill-sky-100" />}
                </div>
                <span className="text-[10px] text-brown-400">{m.created_at ? new Date(m.created_at).toLocaleDateString("da-DK") : ""}</span>
              </div>
              <div className="mt-1 text-xs text-brown-600 flex items-center gap-1"><MapPin className="h-3 w-3 text-gold-500" /> {m.city}{m.country ? `, ${m.country}` : ""}</div>
              {m.profession && <div className="mt-1 text-xs text-brown-500">{m.profession}</div>}
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {m.sect && <span className="text-[10px] px-2 py-0.5 rounded-full bg-olive-100 text-olive-600">{m.sect}</span>}
                {m.religious_practice && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold-100 text-gold-600">{m.religious_practice.replaceAll("_", " ")}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FooterNote() {
  return (
    <div className="mt-12 text-center text-xs text-brown-400">Find Your Naseeb · Med respekt, med intention.</div>
  );
}

function Stat({ label, value, icon, testid }) {
  return (
    <div className="card-soft p-4 text-center" data-testid={testid}>
      <div className="inline-flex items-center gap-1 text-xs text-brown-500 uppercase tracking-widest">{icon} {label}</div>
      <div className="mt-1 font-serif text-2xl text-olive-700">{value}</div>
    </div>
  );
}
